import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { IonBackButton, IonButton, IonButtons, IonContent, IonHeader, IonInput, IonPage, IonSpinner, IonTitle, IonToolbar } from "@ionic/react";
import { motion } from "framer-motion";
import { arrowBack } from "ionicons/icons";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useIonRouter } from "@ionic/react";

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const ionRouter = useIonRouter();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const handleResetPassword = async () => {
    if (!email) {
      setError(t('Auth:email_required') || "Please enter your email address");
      return;
    }

    try {
      setIsLoading(true);
      setError("");

      // Send password reset email using Firebase Authentication
      await FirebaseAuthentication.sendPasswordResetEmail({ email });

      // Show success message
      setSuccessMessage(
        t('Auth:password_reset_email_sent') ||
        "Password reset email sent. Please check your inbox."
      );

      // Automatically redirect back to login after 5 seconds
      setTimeout(() => {
        ionRouter.push('/sign-in');
      }, 5000);

    } catch (err: any) {
      console.error('Error sending password reset email:', err);
      setError(err.message || t('Auth:password_reset_error') || "Failed to send password reset email");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <IonPage>
      <IonHeader className="md:hidden">
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton icon={arrowBack} text="" defaultHref="/sign-in" />
          </IonButtons>
          <IonTitle>{t('Auth:forgot_password') || "Forgot Password"}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="flex flex-col p-3 justify-center bg-background items-center h-full md:bg-[url('/assets/images/bg/bg3.webp')] bg-cover bg-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-md space-y-6 md:bg-card md:p-8 rounded-xl"
          >
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => ionRouter.push('/sign-in')} 
                  className="hidden md:flex items-center justify-center rounded-full w-8 h-8 hover:bg-secondary transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-5">
                    <path d="m15 18-6-6 6-6"/>
                  </svg>
                  <span className="sr-only">Back</span>
                </button>
                <h2 className="text-2xl font-semibold text-foreground">
                  {t('Auth:reset_password') || "Reset Password"}
                </h2>
              </div>
              <p className="text-muted-foreground">
                {t('Auth:reset_password_instructions') ||
                  "Enter your email address and we'll send you instructions to reset your password."}
              </p>

              <div className="space-y-4">
                <IonInput
                  type="email"
                  shape='round'
                  placeholder={t('Auth:email') || "Email"}
                  value={email}
                  className="h-12 rounded-2xl bg-input border border-input"
                  onIonInput={(e) => setEmail(e.detail.value || "")}
                  style={{
                    '--padding-start': '1rem',
                    '--padding-end': '1rem',
                  }}
                />


                {error && (
                  <p className="text-sm text-destructive">{error}</p>
                )}

                {successMessage && (
                  <div className="p-3 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 rounded-md text-sm">
                    {successMessage}
                  </div>
                )}

                {!successMessage && (
                  <IonButton
                    expand="block"
                    shape="round"
                    onClick={handleResetPassword}
                    disabled={isLoading || !!successMessage}
                  >
                    {isLoading ? (
                      <IonSpinner name="dots" />
                    ) : (
                      t('Auth:send_reset_link') || "Send Reset Link"
                    )}
                  </IonButton>
                )}

                {successMessage && (
                  <IonButton
                    expand="block"
                    fill="outline"
                    shape="round"
                    routerLink="/sign-in"
                  >
                    {t('Auth:back_to_login') || "Back to Login"}
                  </IonButton>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default ForgotPassword;
